import React, { useEffect, useState, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const DEFAULT_TEMPLATE = `Dear {{guest_name}},

Welcome to {{hotel_name}}!

Your reservation {{reservation_no}} is confirmed for {{arrival_date}} ({{nights}} night(s), {{room_type}}).

To make your check-in faster, please complete your details in advance:

{{pre_checkin_link}}

This link is valid until your arrival date. You can save your progress and return anytime.

We look forward to welcoming you!

Warm regards,
{{hotel_name}}`;

const PLACEHOLDERS = [
  { key: '{{guest_name}}', label: 'Guest Name' },
  { key: '{{hotel_name}}', label: 'Hotel Name' },
  { key: '{{reservation_no}}', label: 'Reservation No' },
  { key: '{{arrival_date}}', label: 'Arrival Date' },
  { key: '{{nights}}', label: 'Nights' },
  { key: '{{room_type}}', label: 'Room Type' },
  { key: '{{pre_checkin_link}}', label: 'Pre-Check-In Link' },
];

function PmsPreCheckinConfig({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [isEnabled, setIsEnabled] = useState(false);
  const [sendHoursBefore, setSendHoursBefore] = useState(24);
  const [deliveryMode, setDeliveryMode] = useState('SMS');
  const [welcomeTemplate, setWelcomeTemplate] = useState(DEFAULT_TEMPLATE);
  const [tokenValidityBuffer, setTokenValidityBuffer] = useState(6);
  const [portalBaseUrl, setPortalBaseUrl] = useState('');

  const headers = useCallback((withJson = false) => ({
    Authorization: `Bearer ${token}`,
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty]);

  const load = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/v1/pms/pre-checkin/config', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load');
      if (data) {
        setIsEnabled(data.is_enabled || false);
        setSendHoursBefore(data.send_hours_before || 24);
        setDeliveryMode(data.delivery_mode || 'SMS');
        setWelcomeTemplate(data.welcome_template || DEFAULT_TEMPLATE);
        setTokenValidityBuffer(data.token_validity_buffer_hours || 6);
        setPortalBaseUrl(data.portal_base_url || '');
      }
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, headers]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/v1/pms/pre-checkin/config', {
        method: 'POST',
        headers: headers(true),
        body: JSON.stringify({
          is_enabled: isEnabled,
          send_hours_before: sendHoursBefore,
          delivery_mode: deliveryMode,
          welcome_template: welcomeTemplate,
          token_validity_buffer_hours: tokenValidityBuffer,
          portal_base_url: portalBaseUrl,
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to save');
      setMessage('Pre-Check-In configuration saved');
      setTimeout(() => setMessage(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const insertPlaceholder = (ph) => {
    setWelcomeTemplate(prev => prev + ph);
  };

  // Preview
  const previewText = welcomeTemplate
    .replace(/\{\{guest_name\}\}/g, 'John Smith')
    .replace(/\{\{hotel_name\}\}/g, selectedProperty?.name || 'Grand Hotel')
    .replace(/\{\{reservation_no\}\}/g, 'RES-103')
    .replace(/\{\{arrival_date\}\}/g, '2026-03-20')
    .replace(/\{\{nights\}\}/g, '3')
    .replace(/\{\{room_type\}\}/g, 'Deluxe Room')
    .replace(/\{\{pre_checkin_link\}\}/g, 'https://guest.hotelops.in/pre-checkin/HTLGOA/RES-103?key=abc...');

  if (!selectedProperty?.id) {
    return (
      <div className="page-shell">
        <p style={{ color: 'var(--theme-text-secondary)', padding: '2rem', textAlign: 'center' }}>Select a property first</p>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div style={{ marginBottom: '0.2rem', color: 'var(--theme-text-secondary)', fontSize: '0.82rem' }}>Hotel Config &gt; Pre-Check-In</div>
      <h2 style={{ margin: '0 0 0.3rem', color: 'var(--theme-primary-dark)' }}>Guest Pre-Check-In Settings</h2>
      <p style={{ color: 'var(--theme-text-secondary)', margin: '0 0 1rem', fontSize: '0.88rem' }}>
        Enable pre-check-in to send guests a link to fill their details before arrival, reducing front-desk check-in time.
      </p>

      {error && <div className="error-banner" style={{ marginBottom: '0.5rem' }}>{error}</div>}
      {message && <div className="success-banner" style={{ marginBottom: '0.5rem' }}>{message}</div>}

      {loading ? (
        <div style={{ color: 'var(--theme-text-secondary)', padding: '2rem', textAlign: 'center' }}>Loading...</div>
      ) : (
        <div style={{ maxWidth: 750 }}>
          {/* Enable Toggle */}
          <div className="card" style={{ padding: '1rem 1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--theme-primary)' }}>Enable Pre-Check-In</div>
              <div style={{ color: 'var(--theme-text-secondary)', fontSize: '0.82rem', marginTop: '0.2rem' }}>
                {isEnabled
                  ? 'Active — guests will receive pre-check-in links before arrival'
                  : 'Disabled — pre-check-in links will not be sent'}
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={isEnabled} onChange={e => setIsEnabled(e.target.checked)}
                style={{ width: 20, height: 20, cursor: 'pointer' }} />
            </label>
          </div>

          {/* Settings Grid */}
          <div className="card" style={{ padding: '1.2rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '180px 1fr', gap: isMobile ? '0.35rem' : '0.8rem 1.2rem', alignItems: 'center', fontSize: '0.88rem' }}>
              {/* Send Timing */}
              <label className="form-label" style={{ margin: 0 }}>Send Before Arrival</label>
              <select value={sendHoursBefore} onChange={e => setSendHoursBefore(Number(e.target.value))}
                style={{ padding: '0.45rem 0.5rem' }}>
                <option value={6}>6 hours</option>
                <option value={12}>12 hours</option>
                <option value={24}>24 hours (1 day)</option>
                <option value={48}>48 hours (2 days)</option>
              </select>

              {/* Delivery Mode */}
              <label className="form-label" style={{ margin: 0 }}>Delivery Mode</label>
              <select value={deliveryMode} onChange={e => setDeliveryMode(e.target.value)}
                style={{ padding: '0.45rem 0.5rem' }}>
                <option value="SMS">SMS</option>
                <option value="WHATSAPP">WhatsApp</option>
                <option value="BOTH">Both (WhatsApp + SMS fallback)</option>
              </select>

              {/* Token Validity Buffer */}
              <label className="form-label" style={{ margin: 0 }}>Link Valid After Arrival</label>
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                <select value={tokenValidityBuffer} onChange={e => setTokenValidityBuffer(Number(e.target.value))}
                  style={{ padding: '0.45rem 0.5rem' }}>
                  <option value={3}>3 hours</option>
                  <option value={6}>6 hours</option>
                  <option value={12}>12 hours</option>
                  <option value={24}>24 hours</option>
                </select>
                <span style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)' }}>after scheduled arrival</span>
              </div>

              {/* Portal Base URL */}
              <label className="form-label" style={{ margin: 0 }}>Guest Portal URL</label>
              <input type="text" value={portalBaseUrl} onChange={e => setPortalBaseUrl(e.target.value)}
                placeholder="https://guest.yourhotel.com"
                style={{ padding: '0.45rem 0.5rem' }} />
            </div>
          </div>

          {/* Welcome Template */}
          <div className="card" style={{ padding: '1.2rem', marginTop: '1rem' }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', color: 'var(--theme-primary-dark)' }}>Welcome Note Template</h3>
            <p style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)', margin: '0 0 0.5rem' }}>
              This message is sent to guests with their pre-check-in link. Use placeholders below:
            </p>

            {/* Placeholder buttons */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.6rem' }}>
              {PLACEHOLDERS.map(p => (
                <button key={p.key} onClick={() => insertPlaceholder(p.key)}
                  className="btn-muted" style={{ padding: '0.2rem 0.5rem', fontSize: '0.72rem' }}>
                  + {p.label}
                </button>
              ))}
            </div>

            <textarea value={welcomeTemplate} onChange={e => setWelcomeTemplate(e.target.value)}
              rows={10} style={{ width: '100%', padding: '0.6rem', fontSize: '0.85rem', fontFamily: 'monospace',
                borderRadius: 6, border: '1px solid var(--theme-border)', resize: 'vertical' }} />

            {/* Preview */}
            <details style={{ marginTop: '0.8rem' }}>
              <summary style={{ fontSize: '0.82rem', color: 'var(--theme-primary)', cursor: 'pointer', fontWeight: 600 }}>
                Preview with sample data
              </summary>
              <div style={{ marginTop: '0.5rem', padding: '0.8rem', background: '#f8fafc', borderRadius: 6,
                border: '1px solid var(--theme-border)', whiteSpace: 'pre-wrap', fontSize: '0.82rem', lineHeight: 1.6,
                color: 'var(--theme-text)' }}>
                {previewText}
              </div>
            </details>
          </div>

          {/* Save */}
          <div style={{ marginTop: '1.2rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <button onClick={() => setWelcomeTemplate(DEFAULT_TEMPLATE)} className="btn-muted rounded"
              style={{ padding: '0.5rem 1rem' }}>
              Reset Template
            </button>
            <button onClick={handleSave} disabled={saving} className="btn-primary rounded"
              style={{ padding: '0.5rem 1.5rem' }}>
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PmsPreCheckinConfig;
